import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete, Box, Button, Chip, CircularProgress, FormControl, FormControlLabel,
  Grid, Radio, RadioGroup, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { toast } from "react-toastify";
import api from "../../api/client";
import { endpoints } from "../../api/endpoints";
import { PageHeader, SectionCard, StatCard, EmptyState, StatusChip } from "../ui";
import { AutoAbsentChip } from "../Leave/leaveAllocationUi";
import { extractErrorMessage } from "../../utils/errorMessage";
import { sortByLabel } from "../../utils/sortOptions";
import { downloadAttendanceReportExcel } from "../../utils/exportAttendanceReportExcel";

const ALL_OPTION = { id: "all", label: "All Employees" };

const defaultMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function AdminAttendanceLookupNew() {
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(ALL_OPTION);
  const [filterMode, setFilterMode] = useState("month");
  const [month, setMonth] = useState(defaultMonth());
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get(endpoints.employees.list)
      .then((res) => setEmployees(Array.isArray(res.data) ? res.data : res.data?.data || []))
      .catch(() => setEmployees([]));
  }, []);

  const opts = useMemo(
    () => [
      ALL_OPTION,
      ...sortByLabel(
        employees.map((e) => ({ id: e.employeeid, label: `${e.name} (${e.employeeid})` })),
        (o) => o.label
      ),
    ],
    [employees]
  );

  const buildParams = () => {
    const params = { employeeId: selected?.id || "all" };
    if (filterMode === "month") {
      if (!month) throw new Error("Select a month");
      params.month = month;
    } else {
      if (!fromDate || !toDate) throw new Error("Pick both from and to dates");
      params.fromDate = fromDate;
      params.toDate = toDate;
    }
    return params;
  };

  const fetchReport = async () => {
    let params;
    try {
      params = buildParams();
    } catch (err) {
      toast.error(err.message);
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const res = await api.get(endpoints.attendance.adminTeamReport, { params });
      setRows(res.data?.rows || []);
      setSummary(res.data?.summary || null);
    } catch (err) {
      toast.error(extractErrorMessage(err));
      setRows([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    if (!rows.length) return toast.error("No data to export");
    setExporting(true);
    try {
      const prefix = selected?.id === "all" ? "all-employees-attendance" : `attendance-${selected.id}`;
      await downloadAttendanceReportExcel(rows, prefix, {
        splitByEmployee: selected?.id === "all",
      });
      toast.success("Excel downloaded");
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Excel export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box p={3}>
      <PageHeader
        title="Team Attendance Report"
        subtitle="View and export attendance, leave, late arrivals, and half-days for any employee or the whole team"
        icon={<GroupsOutlinedIcon />}
      />

      <SectionCard sx={{ mb: 3 }} accent="#6C50BF">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Autocomplete
              size="small"
              options={opts}
              value={selected}
              onChange={(_, v) => setSelected(v || ALL_OPTION)}
              getOptionLabel={(o) => o?.label || ""}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(p) => (
                <TextField {...p} label="Employee" placeholder="All or search by name / ID" />
              )}
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <FormControl component="fieldset" size="small">
              <RadioGroup
                row
                value={filterMode}
                onChange={(e) => setFilterMode(e.target.value)}
              >
                <FormControlLabel value="month" control={<Radio size="small" />} label="By month" />
                <FormControlLabel value="range" control={<Radio size="small" />} label="By date range" />
              </RadioGroup>
            </FormControl>
          </Grid>

          {filterMode === "month" ? (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="month"
                label="Month"
                InputLabelProps={{ shrink: true }}
                value={month}
                onChange={(e) => setMonth(e.target.value)}
              />
            </Grid>
          ) : (
            <>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="From"
                  InputLabelProps={{ shrink: true }}
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="To"
                  InputLabelProps={{ shrink: true }}
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  inputProps={{ min: fromDate }}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              onClick={fetchReport}
              disabled={loading}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </SectionCard>

      {searched && summary && !loading && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} md={2.4}><StatCard label="Records" value={summary.totalRows} accent="#6C50BF" /></Grid>
          <Grid item xs={6} md={2.4}><StatCard label="Employees" value={summary.employees} accent="#5C85FF" /></Grid>
          <Grid item xs={6} md={2.4}><StatCard label="Present / WFH" value={summary.present} accent="#39C645" /></Grid>
          <Grid item xs={6} md={2.4}><StatCard label="Half Days" value={summary.halfDay} accent="#F9BC06" /></Grid>
          <Grid item xs={6} md={2.4}><StatCard label="Late / Leave" value={`${summary.late} / ${summary.onLeave}`} accent="#EF4444" /></Grid>
        </Grid>
      )}

      {searched && (
        <SectionCard
          title="Attendance & Leave Records"
          subtitle={selected?.id === "all" ? "All employees" : selected?.label}
          actions={rows.length > 0 ? [
            <Button
              key="excel"
              variant="contained"
              size="small"
              startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <DownloadOutlinedIcon />}
              onClick={exportExcel}
              disabled={exporting}
            >
              Download Excel
            </Button>,
          ] : []}
        >
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
          ) : rows.length === 0 ? (
            <EmptyState title="No records found" subtitle="Try a different employee or date range." />
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {[
                      "Employee", "Department", "Date", "Day", "Check In", "Check Out",
                      "Hours", "Late", "Half Day", "Status", "WFH", "Leave", "Notes",
                    ].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", whiteSpace: "nowrap" }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, idx) => (
                    <TableRow key={`${r.employeeId}-${r.dateKey}-${idx}`} hover>
                      <TableCell sx={{ fontSize: 12, minWidth: 140 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{r.employeeName}</Typography>
                        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{r.employeeId}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{r.department}</TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{r.date}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>{r.day}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{r.checkIn}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{r.checkOut}</TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{r.workingHours}</TableCell>
                      <TableCell>
                        {r.late === "Yes" ? <Chip label="Late" size="small" color="error" sx={{ height: 20, fontSize: 10 }} /> : "—"}
                      </TableCell>
                      <TableCell>
                        {r.halfDay === "Yes" ? <Chip label="Half" size="small" color="warning" sx={{ height: 20, fontSize: 10 }} /> : "—"}
                      </TableCell>
                      <TableCell><StatusChip status={r.dayStatus} /></TableCell>
                      <TableCell>{r.wfh === "Yes" ? <Chip label="WFH" size="small" sx={{ height: 20, fontSize: 10 }} /> : "—"}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {r.leaveType ? (
                          <Stack spacing={0.25}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{r.leaveType}</Typography>
                            <Typography sx={{ fontSize: 10, color: "text.secondary" }}>{r.leaveStatus}</Typography>
                            {r.autoGenerated ? <AutoAbsentChip /> : null}
                          </Stack>
                        ) : "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, color: "text.secondary", maxWidth: 220 }}>{r.notes}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </SectionCard>
      )}
    </Box>
  );
}
